import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api";
import { CommandBar } from "../Shell";
import { ErrorBox } from "../ui";
import { Icon } from "../icons";
import { SUITE } from "../suite";
import { reloadLinks, useCompanyLinks } from "../links";

// Common systems, to make adding one a click. Each company's URL differs, so the address is filled in by the user.
const SUGGESTIONS: Record<string, string[]> = {
  people: ["Payroll", "Benefits", "Time off", "Employee handbook", "Org chart", "Careers"],
  it: ["IT help desk", "Equipment request", "Password reset", "Software request"],
  workplace: ["Intranet", "Company calendar", "Lunch menu"],
  requests: ["Facilities request", "Travel booking", "Expense reports"],
  safety: ["OSHA logs", "SDS library"],
  training: ["Learning portal"],
};

export function LinksPage() {
  const [params, setParams] = useSearchParams();
  const company = useCompanyLinks();
  const [area, setArea] = useState(params.get("area") ?? "people");
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const areaName = (id: string) => SUITE.find((d) => d.id === id)?.name ?? id;

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const full = /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
      await api.addLink({ area, name: name.trim(), url: full });
      setName(""); setUrl(""); await reloadLinks();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  const remove = async (id: string) => {
    setError(null);
    try { await api.deleteLink(id); await reloadLinks(); } catch (err) { setError(err); }
  };

  return (
    <>
      <CommandBar status="Admin · shared with everyone in your company" />
      <div className="page">
        <div className="page-title">
          <h1>Company links</h1>
          <span className="sub">Put every system your people use in one navigation: HR, payroll, the IT help desk, the intranet. Links open in a new tab.</span>
        </div>
        <div className="grid cols-main">
          <section className="card">
            <div className="card-head"><h2>Add a link</h2></div>
            <form className="card-body" onSubmit={add}>
              <div className="field">
                <label className="label" htmlFor="area">Area</label>
                <select id="area" className="select" value={area} onChange={(e) => { setArea(e.target.value); setParams({ area: e.target.value }, { replace: true }); }}>
                  {SUITE.filter((d) => d.id !== "admin").map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              {SUGGESTIONS[area] && (
                <div className="bc-row" style={{ marginTop: 0, marginBottom: 12 }}>
                  <span className="bc-tag">Common</span>
                  {SUGGESTIONS[area].map((s) => <button type="button" key={s} className="bc-link" style={{ border: 0, cursor: "pointer" }} onClick={() => setName(s)}>{s}</button>)}
                </div>
              )}
              <div className="field">
                <label className="label" htmlFor="lname">Name</label>
                <input id="lname" className="input" required maxLength={80} placeholder="e.g. Payroll (ADP)" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="field">
                <label className="label" htmlFor="lurl">Web address</label>
                <input id="lurl" className="input" required inputMode="url" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} />
                <span className="hint">Paste the address you use to sign in to that system.</span>
              </div>
              <ErrorBox error={error} />
              <button className="btn primary" disabled={busy || !name.trim() || !url.trim()}>Add to {areaName(area)}</button>
            </form>
          </section>

          <section className="card">
            <div className="card-head"><h2>Your links</h2><span className="muted" style={{ fontSize: 12 }}>{company.length}</span></div>
            {company.length ? (
              <div className="table-wrap">
                <table className="dgrid">
                  <thead><tr><th>Name</th><th>Area</th><th aria-label="Actions" /></tr></thead>
                  <tbody>
                    {company.map((l) => (
                      <tr key={l.id}>
                        <td><a href={l.url} target="_blank" rel="noopener noreferrer">{l.name} <Icon name="ext" size={12} /></a><br /><span className="muted mono" style={{ fontSize: 11 }}>{l.url}</span></td>
                        <td>{areaName(l.area)}</td>
                        <td style={{ textAlign: "right" }}><button className="btn" onClick={() => remove(l.id)} aria-label={`Remove ${l.name}`}>Remove</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="card-body muted">No links yet. Start with payroll and the IT help desk.</div>}
          </section>
        </div>
      </div>
    </>
  );
}
